'use client';

import { c as _c } from "react/compiler-runtime";
import type { VariantProps } from 'class-variance-authority';
import type { ReactElement, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconTooltipArrow } from '@/app/components/icons/ui-actions/icon-tooltip-arrow';
import { getAutoPlacement } from '@/app/components/overlays/tooltip/getAutoPlacement';
import { useClickOutside } from '@/app/hooks/useClickOutside';
import { useMountTransition } from '@/app/hooks/useMountTransition';
import { cn } from '@/app/utils/cn';
import { cnva } from '@/app/utils/cnva';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { safeCall } from '@/app/utils/safeCall';
import styles from './style.module.css';
export const DEFAULT_PLACEMENT = 'top-right';
const DEFAULT_AUTO_CLOSE_SECOND = 3;
const ARROW_PLACEMENT_CLASSNAME = {
  top: styles.tooltipArrowTop,
  bottom: cn('rotate-180', styles.tooltipArrowBottom),
  center: cn('justify-center')
};
const TooltipArrowStyled = cnva('absolute left-0 flex h-2 w-full items-center transition-opacity duration-300', {
  variants: {
    theme: {
      light: 'text-gray-50',
      dark: 'text-gray-800',
      custom: ''
    },
    arrowPlacement: {
      'top-right': ARROW_PLACEMENT_CLASSNAME.top,
      'top-left': ARROW_PLACEMENT_CLASSNAME.top,
      'bottom-right': ARROW_PLACEMENT_CLASSNAME.bottom,
      'bottom-left': ARROW_PLACEMENT_CLASSNAME.bottom,
      'top-center': [ARROW_PLACEMENT_CLASSNAME.top, ARROW_PLACEMENT_CLASSNAME.center],
      'bottom-center': [ARROW_PLACEMENT_CLASSNAME.bottom, ARROW_PLACEMENT_CLASSNAME.center]
    },
    arrowCenter: {
      true: 'justify-center',
      false: ''
    },
    isVisible: {
      true: 'opacity-100',
      false: 'opacity-0'
    }
  },
  compoundVariants: [{
    arrowCenter: false,
    arrowPlacement: ['top-right', 'bottom-left'],
    className: 'justify-end pr-2.5'
  }, {
    arrowCenter: false,
    arrowPlacement: ['top-left', 'bottom-right'],
    className: 'pl-2.5'
  }],
  defaultVariants: {
    isVisible: false,
    arrowCenter: true,
    // Default is to center the arrow position from the trigger element
    arrowPlacement: DEFAULT_PLACEMENT,
    theme: 'light'
  }
});
const TOOLTIP_PLACEMENT_CLASSNAME = {
  top: styles.tooltipBoxTop,
  bottom: styles.tooltipBoxBottom,
  center: cn('center-x'),
  left: cn('left-0'),
  right: cn('right-0')
};
const TooltipStyled = cnva('absolute', {
  variants: {
    theme: {
      light: 'bg-gray-50',
      dark: 'bg-gray-800 text-white',
      custom: ''
    },
    placement: {
      'top-center': [TOOLTIP_PLACEMENT_CLASSNAME.top, TOOLTIP_PLACEMENT_CLASSNAME.center],
      'top-left': [TOOLTIP_PLACEMENT_CLASSNAME.top, TOOLTIP_PLACEMENT_CLASSNAME.left],
      'top-right': [TOOLTIP_PLACEMENT_CLASSNAME.top, TOOLTIP_PLACEMENT_CLASSNAME.right],
      'bottom-center': [TOOLTIP_PLACEMENT_CLASSNAME.bottom, TOOLTIP_PLACEMENT_CLASSNAME.center],
      'bottom-right': [TOOLTIP_PLACEMENT_CLASSNAME.bottom, TOOLTIP_PLACEMENT_CLASSNAME.right],
      'bottom-left': [TOOLTIP_PLACEMENT_CLASSNAME.bottom, TOOLTIP_PLACEMENT_CLASSNAME.left]
    }
  },
  compoundVariants: [{
    theme: ['light', 'dark'],
    className: 'rounded-2xs text-body-1-fixed w-71 px-4 py-3'
  }],
  defaultVariants: {
    placement: DEFAULT_PLACEMENT,
    theme: 'light'
  }
});
type TooltipStylesProps = VariantProps<typeof TooltipStyled>;
type TooltipPlacementTheme = TooltipStylesProps['theme'];
export type TooltipPlacementType = TooltipStylesProps['placement'];
export interface TooltipProps {
  text?: string;
  element?: ReactElement | null;
  placement?: TooltipPlacementType;
  theme?: TooltipPlacementTheme;
  show?: boolean; // Controlled visibility (when manualShow is true)
  manualShow?: boolean; // If true, `show` prop controls visibility
  autoPlacement?: boolean; // If true, tooltip attempts to auto-position
  autoClose?: boolean; // If true, tooltip auto-closes after a duration or on outside click
  autoCloseDelay?: number;
  disableClickOutside?: boolean; // If true, tooltip auto-closes after a duration or on outside click
  showDelay?: number; // Delay in ms before showing
  children: ReactNode; // Trigger element (required)
  wrapperClassName?: string;
  tooltipClassName?: string;
  arrowClassName?: string;
  arrowCenter?: VariantProps<typeof TooltipArrowStyled>['arrowCenter'];
  showOnHoverOnly?: boolean;
  onClose?: () => void;
}
export const Tooltip = t0 => {
  const $ = _c(54);
  const {
    text,
    element,
    show: t1,
    manualShow: t2,
    theme: t3,
    placement: t4,
    autoPlacement: t5,
    autoClose: t6,
    autoCloseDelay: t7,
    disableClickOutside: t8,
    arrowCenter: t9,
    showDelay: t10,
    children,
    wrapperClassName,
    tooltipClassName,
    arrowClassName,
    showOnHoverOnly: t11,
    onClose
  } = t0;
  const show = t1 === undefined ? false : t1;
  const manualShow = t2 === undefined ? false : t2;
  const theme = t3 === undefined ? "light" : t3;
  const placement = t4 === undefined ? DEFAULT_PLACEMENT : t4;
  const autoPlacement = t5 === undefined ? false : t5;
  const autoClose = t6 === undefined ? true : t6;
  const autoCloseDelay = t7 === undefined ? DEFAULT_AUTO_CLOSE_SECOND : t7;
  const disableClickOutside = t8 === undefined ? false : t8;
  const arrowCenter = t9 === undefined ? true : t9;
  const showDelay = t10 === undefined ? 0 : t10;
  const showOnHoverOnly = t11 === undefined ? false : t11;
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const openTimeoutId = useRef(undefined);
  const [calculatedPlacement, setCalculatedPlacement] = useState(undefined);
  const [isVisible, setIsVisible] = useState(false);
  let t12;
  if ($[0] !== isVisible) {
    t12 = {
      ref: tooltipRef,
      isMount: isVisible,
      baseClassName: "transition-opacity duration-300 ease-bounce opacity-0",
      animateClassName: "opacity-100"
    };
    $[0] = isVisible;
    $[1] = t12;
  } else {
    t12 = $[1];
  }
  const {
    isTransitioning,
    transitionState,
    className: transitionClassName
  } = useMountTransition(t12);
  const computedPlacement = calculatedPlacement ?? placement;
  let t13;
  if ($[2] !== showDelay) {
    t13 = () => {
      clearTimeout(openTimeoutId.current);
      if (showDelay > 0) {
        openTimeoutId.current = setTimeout(() => {
          setIsVisible(true);
        }, showDelay);
      } else {
        setIsVisible(true);
      }
    };
    $[2] = showDelay;
    $[3] = t13;
  } else {
    t13 = $[3];
  }
  const showTooltip = t13;
  let t14;
  if ($[4] !== onClose) {
    t14 = () => {
      setIsVisible(false);
      safeCall(onClose);
      clearTimeout(openTimeoutId.current);
    };
    $[4] = onClose;
    $[5] = t14;
  } else {
    t14 = $[5];
  }
  const closeTooltip = t14;
  let t15;
  bb0: {
    if (disableClickOutside) {
      t15 = false;
      break bb0;
    }
    if (manualShow && show) {
      t15 = !autoClose;
      break bb0;
    }
    t15 = isVisible;
  }
  const isEnableClickOutside = t15;
  let t16;
  if ($[6] !== manualShow || $[7] !== showOnHoverOnly || $[8] !== showTooltip) {
    t16 = showOnHoverOnly ? {
      onMouseOver: () => setIsVisible(true),
      onMouseOut: () => setIsVisible(false)
    } : {
      onMouseEnter: manualShow ? undefined : showTooltip,
      onClick: manualShow ? undefined : showTooltip
    };
    $[6] = manualShow;
    $[7] = showOnHoverOnly;
    $[8] = showTooltip;
    $[9] = t16;
  } else {
    t16 = $[9];
  }
  const triggerEvents = t16;
  let t17;
  if ($[10] !== autoPlacement || $[11] !== isVisible || $[12] !== placement || $[13] !== transitionState) {
    t17 = () => {
      if (!autoPlacement || !triggerRef.current || !tooltipRef.current) {
        return;
      }
      if (transitionState === "animate" && isVisible) {
        const result = getAutoPlacement(tooltipRef.current, triggerRef.current, placement);
        setCalculatedPlacement(result ?? DEFAULT_PLACEMENT);
      }
    };
    $[10] = autoPlacement;
    $[11] = isVisible;
    $[12] = placement;
    $[13] = transitionState;
    $[14] = t17;
  } else {
    t17 = $[14];
  }
  let t18;
  if ($[15] !== autoPlacement || $[16] !== isVisible || $[17] !== placement || $[18] !== text || $[19] !== transitionState) {
    t18 = [transitionState, triggerRef, tooltipRef, isVisible, text, placement, autoPlacement];
    $[15] = autoPlacement;
    $[16] = isVisible;
    $[17] = placement;
    $[18] = text;
    $[19] = transitionState;
    $[20] = t18;
  } else {
    t18 = $[20];
  }
  useEffect(t17, t18);
  let t19;
  if ($[21] !== isEnableClickOutside) {
    t19 = {
      enable: isEnableClickOutside
    };
    $[21] = isEnableClickOutside;
    $[22] = t19;
  } else {
    t19 = $[22];
  }
  useClickOutside(triggerRef, closeTooltip, t19);
  let t20;
  let t21;
  if ($[23] !== autoClose || $[24] !== autoCloseDelay || $[25] !== isVisible || $[26] !== onClose) {
    t20 = () => {
      if (!isVisible || !autoClose) {
        return;
      }
      const timeoutId = setTimeout(() => {
        setIsVisible(false);
        safeCall(onClose);
      }, (autoCloseDelay || DEFAULT_AUTO_CLOSE_SECOND) * 1000);
      return () => {
        clearTimeout(timeoutId);
      };
    };
    t21 = [isVisible, autoClose, autoCloseDelay, onClose];
    $[23] = autoClose;
    $[24] = autoCloseDelay;
    $[25] = isVisible;
    $[26] = onClose;
    $[27] = t20;
    $[28] = t21;
  } else {
    t20 = $[27];
    t21 = $[28];
  }
  useEffect(t20, t21);
  let t22;
  let t23;
  if ($[29] !== closeTooltip || $[30] !== manualShow || $[31] !== show || $[32] !== showTooltip) {
    t22 = () => {
      if (!manualShow) {
        return;
      }
      if (show) {
        showTooltip();
      } else {
        closeTooltip();
      }
    };
    t23 = [manualShow, show, showTooltip, closeTooltip];
    $[29] = closeTooltip;
    $[30] = manualShow;
    $[31] = show;
    $[32] = showTooltip;
    $[33] = t22;
    $[34] = t23;
  } else {
    t22 = $[33];
    t23 = $[34];
  }
  useEffect(t22, t23);
  let t24;
  if ($[35] !== wrapperClassName) {
    t24 = cn("group relative inline-flex outline-none", wrapperClassName);
    $[35] = wrapperClassName;
    $[36] = t24;
  } else {
    t24 = $[36];
  }
  let t25;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = createDataAttributes({
      test: "tooltip-wrapper"
    });
    $[37] = t25;
  } else {
    t25 = $[37];
  }
  let t26;
  if ($[38] !== arrowCenter || $[39] !== arrowClassName || $[40] !== computedPlacement || $[41] !== element || $[42] !== isTransitioning || $[43] !== isVisible || $[44] !== text || $[45] !== theme || $[46] !== tooltipClassName || $[47] !== transitionClassName) {
    t26 = !!isTransitioning && <>{!!element ? <div ref={tooltipRef} role="tooltip" className={TooltipStyled({
        theme: "custom",
        placement: computedPlacement,
        className: cn(transitionClassName, tooltipClassName)
      })} {...createDataAttributes({
        test: "tooltip"
      })}>{element}</div> : !!text && <div ref={tooltipRef} role="tooltip" className={TooltipStyled({
        theme,
        placement: computedPlacement,
        className: cn(transitionClassName, tooltipClassName)
      })} dangerouslySetInnerHTML={{
        __html: text
      }} {...createDataAttributes({
        test: "tooltip"
      })} />}<div className={TooltipArrowStyled({
        theme: !!element ? "custom" : theme,
        isVisible,
        arrowCenter,
        arrowPlacement: computedPlacement,
        className: cn(transitionClassName, arrowClassName)
      })}><IconTooltipArrow /></div></>;
    $[38] = arrowCenter;
    $[39] = arrowClassName;
    $[40] = computedPlacement;
    $[41] = element;
    $[42] = isTransitioning;
    $[43] = isVisible;
    $[44] = text;
    $[45] = theme;
    $[46] = tooltipClassName;
    $[47] = transitionClassName;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] !== children || $[50] !== t24 || $[51] !== t26 || $[52] !== triggerEvents) {
    t27 = <div ref={triggerRef} className={t24} {...t25} {...triggerEvents}>{children}{t26}</div>;
    $[49] = children;
    $[50] = t24;
    $[51] = t26;
    $[52] = triggerEvents;
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  return t27;
};